// ============================================
// components/FollowListModal.tsx
// 关注 / 粉丝 名单弹窗. 点个人主页的"关注/粉丝"数字打开.
// ============================================

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import Avatar from './Avatar';
import { authedCallApi } from '../services/auth';

interface UserItem {
  short_id: string | null;
  nickname: string;
  avatar_url: string | null;
  level: number;
}

interface Props {
  shortId: string;            // 看谁的名单
  kind: 'following' | 'followers';
  onClose: () => void;
}

export default function FollowListModal({ shortId, kind, onClose }: Props) {
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserItem[]>([]);
  const [loading, setLoading] = useState(true);

  const title = kind === 'following' ? '关注' : '粉丝';
  const action = kind === 'following' ? 'community-list-following' : 'community-list-followers';

  useEffect(() => {
    setLoading(true);
    authedCallApi<{ users: UserItem[] }>(action, { short_id: shortId })
      .then((r) => {
        if (r.ok && Array.isArray(r.users)) setUsers(r.users as UserItem[]);
      })
      .finally(() => setLoading(false));
  }, [shortId, action]);

  const go = (sid: string | null) => {
    if (!sid) return;
    onClose();
    navigate(`/u/${sid}`);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/35 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-sm max-h-[70vh] flex flex-col shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-ink-300/30">
          <h3 className="font-bold text-ink-900">{title}</h3>
          <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-ink-100 text-ink-400">
            <X className="w-4.5 h-4.5" />
          </button>
        </div>

        <div className="overflow-y-auto p-2">
          {loading ? (
            <div className="py-12 text-center text-ink-400 text-sm">加载中…</div>
          ) : users.length === 0 ? (
            <div className="py-12 text-center text-ink-400 text-sm">
              {kind === 'following' ? '还没有关注任何人' : '还没有粉丝'}
            </div>
          ) : (
            users.map((u, i) => (
              <button
                key={u.short_id || i}
                onClick={() => go(u.short_id)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gold-soft transition-colors text-left"
              >
                <Avatar name={u.nickname} size={40} className="!rounded-full shrink-0" />
                <div className="min-w-0">
                  <div className="font-medium text-ink-900 truncate">{u.nickname}</div>
                  <div className="text-xs text-ink-400">LV{u.level}</div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
